import { useState, useEffect, useCallback } from 'react';
import { pm2SitemapStore, getPagePathById, findPageDescriptorByPath } from '@pm2/sitemap-utils';
import { pm2ConfigStore } from '@pm2/store';
import { useNavigationManager } from '@provider/navigation';
import { useUserIdentityState } from '@provider/user-identity';
import { breakpoints } from '@ui/css';
import { loadNotebookLibrary } from '@utils/common';
import { useSelf } from '@utils/react';
import { ASIDE_TAKEOVER_BREAKPOINT_PX } from '../../aside/styles/Aside.js';
import { parseNotePlacementInfo } from './utils/parseNotePlacementInfo.js';
import { stringifyNotePlacementInfo } from './utils/stringifyNotePlacementInfo.js';

let autoIncrementedId = 0;
/**
 * apply low level integrations with the gNotebook library to provide high
 * level React tools for further integration into the application.
 */
function useNotebook(notebookRef) {
  const navigation = useNavigationManager();
  const user = useUserIdentityState(state => state.user);
  const configStore = pm2ConfigStore.useStore();
  const sitemapStore = pm2SitemapStore.useStore();
  const portalConfig = pm2ConfigStore.useSelector(state => state.config);
  const [backlinkContext, setBacklinkContext] = useState(undefined);
  const [notes, setNotes] = useState([]);
  const [isUnavailable, setUnavailable] = useState(!user && !portalConfig.settings.loginDisabled);
  const [isOpen, setOpen] = useState(false);
  const [isExpanded, setExpanded] = useState(false);
  const self = useSelf({
    configStore,
    sitemapStore,
    ref: notebookRef,
    context: createContextFromPath(sitemapStore, navigation.getLocation().pathname),
    setOpen,
    setExpanded,
    setNotes,
    setBacklinkContext,
    navigate: (path, elementId) => {
      const fullPath = elementId ? `${path}#${elementId}&bust=${autoIncrementedId++}` : path;
      return navigation.getLocation().pathname === path ? navigation.replace(fullPath) : navigation.push(fullPath);
    }
  });
  useEffect(() => {
    setUnavailable(!user && !portalConfig.settings.loginDisabled);
  }, [portalConfig.settings.loginDisabled, user]);

  // initialize the gNotebook when the component is mounted into the DOM to
  // perform post loading of the associated scripts
  useEffect(() => {
    if (!user) {
      return;
    }
    const onRequestOpen = handleRequestOpen.bind(null, self);
    const onRequestClose = handleRequestClose.bind(null, self);
    const onBacklink = handleBacklink.bind(null, self);
    const onUpdateNotes = handleUpdateNotes.bind(null, self);
    loadNotebookLibrary().then(gNotebook => {
      // configure id of the product so that notes are properly associated
      // with the notebook for future reference
      gNotebook.setProduct({
        id: portalConfig.uniqueId,
        title: portalConfig.title
      });
      gNotebook.setContext(self.context);
      gNotebook.setInstitutionId(user.institution?.id ?? "");
      gNotebook.toggleGeogebra(portalConfig.settings.enableGeoGebra);

      // handle opening / closing the notebook through the built-in events to
      // support things such as nested notebook panels etc.
      gNotebook.on("request:open", onRequestOpen);
      gNotebook.on("request:close", onRequestClose);

      // handle support for backlink buttons inside the notebook, which will
      // bring the user straight to the linked page
      gNotebook.on("backlink", onBacklink);

      // load notes and keep them up-to-date whenever the user performs any
      // changes
      gNotebook.loadNotes().then(setNotes);
      gNotebook.on("update:notes", onUpdateNotes);
    }).catch(err => {
      // hide the notebook if an error occurs while trying to load the script
      setUnavailable(true);
      throw err;
    });
    return () => {
      loadNotebookLibrary().then(gNotebook => {
        gNotebook.off("request:open", onRequestOpen);
        gNotebook.off("request:close", onRequestClose);
        gNotebook.off("backlink", onBacklink);
        gNotebook.off("update:notes", onUpdateNotes);
      });
    };
  }, [portalConfig, user, self, notebookRef]);

  // auto-update notebook context when the user navigates the portal, so newly
  // created notes are automatically associated with the page on which they were
  // made
  useEffect(() => {
    if (!user) {
      return;
    }
    return navigation.subscribe(() => {
      loadNotebookLibrary().then(gNotebook => {
        self.context = createContextFromPath(sitemapStore, navigation.getLocation().asPath);
        gNotebook.setContext(self.context);
      });
    });
  }, [navigation, self, sitemapStore, user]);
  const open = useCallback(params => {
    loadNotebookLibrary().then(gNotebook => gNotebook.requestOpen(params));
  }, []);
  const close = useCallback(() => {
    loadNotebookLibrary().then(gNotebook => gNotebook.requestClose());
  }, []);
  return {
    isOpen,
    isExpanded,
    isUnavailable,
    isDisabled: portalConfig.settings.loginDisabled,
    notes,
    backlinkContext,
    resetBacklinkContext: useCallback(() => setBacklinkContext(undefined), []),
    open,
    close
  };
}

/**
 * handles the gNotebook request:open event, automatically applying the
 * requested changes.
 */
function handleRequestOpen(self, params) {
  if (typeof gNotebook === "undefined") {
    throw new Error("gNotebook unavailable");
  }
  self.setOpen(true);
  if (self.notebook) {
    self.notebook.setContext(self.context);

    // if the notebook is already open, then simple request that it navigates to
    // the new note
    if (params.noteId) {
      self.notebook.openNote(params.noteId);
    } else if (params.templateId) {
      self.notebook.openTemplate(params.templateId);
    }
    return;
  }

  // ... otherwise instantiate a new instance of the notebook, and mount it into
  // the DOM once available
  self.notebook = new gNotebook({
    ...params,
    canExpand: window.innerWidth > ASIDE_TAKEOVER_BREAKPOINT_PX,
    context: self.context
  });
  requestAnimationFrame(() => {
    if (self.notebook && self.ref.current) {
      self.notebook.mountTo(self.ref.current);
    }
  });

  // add support for expanding / collapsing the notebook to become
  // a full-screen view
  self.notebook.on("expand", () => {
    self.setExpanded(true);
  });
  self.notebook.on("collapse", () => {
    self.setExpanded(false);
  });
}

/**
 * handles gNotebook request:close events and unmounts the notebook iframe
 * instance and resets internal state.
 */
function handleRequestClose(self) {
  if (!self.notebook) {
    return;
  }
  self.notebook.unmount();
  self.notebook = undefined;
  self.setOpen(false);
  self.setExpanded(false);
}

/**
 * handles gNotebook backlink event and navigates the user to the requested
 * page on the current portal (if possible).
 */
async function handleBacklink(self, context) {
  if (!context) {
    // give up if no context was provided on this note; we cannot do anything in
    // that case anyway
    return;
  }
  const placement = parseNotePlacementInfo(context.placementInfo);
  const elementId = placement && "ranges" in placement ? placement.ranges?.[0]?.elementId : undefined;

  // On mobile, where we take over the entire viewport with the
  // notebook, it should be closed when navigating to the context.
  if (window.matchMedia?.(breakpoints.mobile.andDown().toQuery()).matches) {
    self.setOpen(false);
  }
  if (context.learningObject) {
    // If we're dealing with a learning object, then navigate directly to this
    // element on a new page
    self.navigate(`/learningObject/${context.learningObject.id}`, elementId);
    self.setBacklinkContext(context);
    return;
  }
  if (context.page) {
    // ... otherwise resolve page id --> path and navigate the user there if we
    // were successful
    const pageDescriptor = await getPagePathById(self.configStore, self.sitemapStore.getState(), context.page.id);
    if (pageDescriptor) {
      self.navigate(pageDescriptor, elementId);
      self.setBacklinkContext(context);
      return;
    }
  }
  if (context.placementInfo) {
    // finally attempt to extract path from the placementInfo string if nothing
    // else has worked out
    if (placement && placement.path) {
      self.navigate(placement.path, elementId);
      self.setBacklinkContext(context);
      return;
    }
  }

  // if we get here, an error occurred - we couldn't resolve backlink properly
  throw new Error("handleBacklink(): Unable to resolve path from context");
}

/**
 * handle the gNotebook update:notes event and make sure that the in-memory
 * replica of the collection of notes on the portal is updated so we can
 * perform a fresh copy of notes on the page.
 */
async function handleUpdateNotes(self) {
  if (typeof gNotebook === "undefined") {
    throw new Error("gNotebook unavailable");
  }
  self.setNotes([...(await gNotebook.loadNotes())]);
}

/**
 * utility to create gNotebook context based on current state store.
 */
function createContextFromPath(sitemapStore, path) {
  const pageDescriptor = findPageDescriptorByPath(sitemapStore.getState(), path);
  return {
    page: pageDescriptor ? {
      id: pageDescriptor.id,
      title: pageDescriptor.title
    } : undefined,
    placementInfo: stringifyNotePlacementInfo({
      path
    })
  };
}

export { useNotebook };
//# sourceMappingURL=hook.notebook.js.map
